import { useState, useCallback } from 'react';
import { usersApi } from '../api';
import { useFetch } from '../hooks/useFetch';
import toast from 'react-hot-toast';

function UserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState(user ? { name: user.name, email: user.email, phone: user.phone || '', role: user.role, isActive: user.isActive, password: '' } : { name: '', email: '', phone: '', role: 'staff', isActive: true, password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (user) await usersApi.update(user.id, payload);
      else await usersApi.create(payload);
      toast.success(user ? 'User updated' : 'User created'); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h3>{user ? 'Edit User' : 'New User'}</h3><button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-2">
              <div className="form-group"><label className="form-label">Full Name *</label><input className="form-control" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Email *</label><input type="email" className="form-control" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div className="form-row form-row-2">
              <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Role *</label>
                <select className="form-control" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="admin">Admin</option><option value="manager">Manager</option><option value="staff">Staff</option><option value="tailor">Tailor</option>
                </select>
              </div>
            </div>
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">{user ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input type="password" className="form-control" required={!user} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.value === 'true' })}>
                  <option value="true">Active</option><option value="false">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : (user ? 'Update' : 'Create')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Users() {
  const [modal, setModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [roleFilter, setRoleFilter] = useState('');

  const fetchUsers = useCallback(() => usersApi.getAll({ role: roleFilter, limit: 100 }), [roleFilter]);
  const { data, loading, refetch } = useFetch(fetchUsers, [roleFilter]);
  const users = data?.users || data || [];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">User Management</h1>
        <button className="btn btn-primary" onClick={() => { setSelected(null); setModal(true); }}>+ New User</button>
      </div>

      <div className="filters-bar">
        <select className="form-control" style={{ width: 180 }} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          <option value="admin">Admin</option><option value="manager">Manager</option><option value="staff">Staff</option><option value="tailor">Tailor</option>
        </select>
      </div>

      <div className="card">
        {loading ? <div className="empty-state"><div className="spinner" /></div> : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={6}><div className="empty-state"><div className="empty-icon">⚙️</div><p>No users found</p></div></td></tr>
                ) : users.map((u) => (
                  <tr key={u.id}>
                    <td className="font-semibold">{u.name}</td>
                    <td className="text-sm text-muted">{u.email}</td>
                    <td className="mono text-sm">{u.phone || '—'}</td>
                    <td><span className={`badge badge-${u.role}`}>{u.role}</span></td>
                    <td>{u.isActive ? <span className="badge badge-ready">Active</span> : <span className="badge badge-cancelled">Inactive</span>}</td>
                    <td><button className="btn btn-secondary btn-sm" onClick={() => { setSelected(u); setModal(true); }}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <UserModal user={selected} onClose={() => setModal(false)} onSaved={refetch} />}
    </div>
  );
}
